import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import {
  LoginResponseDto,
  RegisterDeviceRequestDto,
  ValidateTokenRequestDto,
} from '@tiendanube/common';
import { PlatformPush as PlatformPushEnum } from '@tiendanube/common/src/enums';
import axios from 'App/axios';
import { i18nConfig, Locale } from 'App/i18n/enums';
import { getStorage, setStorage } from 'App/storage';
import { logError } from 'commons/logger';

export const DEVICE_INFO = 'deviceInfo';
const AUTH = 'auth';
const IS_NATIVE_PLATFORM = Capacitor.isNativePlatform();
const MIN_FCM_TOKEN_LENGTH = 100;

const isValidFcmToken = (token = '') => token.length >= MIN_FCM_TOKEN_LENGTH;

export type DeviceStorageType = {
  token: string;
  deviceId: string;
};

const hasDeviceTokenInStorage = async (
  forceUpdate = false,
): Promise<boolean> => {
  const deviceInfo = await getStorage<DeviceStorageType>(DEVICE_INFO);
  const isValidToken = isValidFcmToken(deviceInfo?.token);

  if (deviceInfo && (forceUpdate || !isValidToken)) {
    // if there is a token in storage that's not from FCM we remove it and regenerate.
    await saveDeviceInfo({ ...deviceInfo, token: '' });
    return false;
  }

  return deviceInfo && !!deviceInfo?.token && isValidToken;
};

export const checkAndRegisterDevice = async (
  forceUpdate = false,
): Promise<void> => {
  if (!IS_NATIVE_PLATFORM) return;

  const permissions = await PushNotifications.requestPermissions();

  const hasToken = await hasDeviceTokenInStorage(forceUpdate);
  if (hasToken) return;

  if (permissions.receive === 'granted') {
    PushNotifications.register();
  }
};

export const makeDevicesMsPayload =
  async (): Promise<ValidateTokenRequestDto> => {
    const deviceInfoPromise = getStorage<DeviceStorageType>(DEVICE_INFO);
    const authPromise = getStorage<LoginResponseDto>(AUTH);
    const localePromise = getStorage<Locale>(i18nConfig.STORAGE);
    const [deviceInfo, { storeId }, locale] = await Promise.all([
      deviceInfoPromise,
      authPromise,
      localePromise,
    ]);

    if (
      !deviceInfo ||
      !deviceInfo.deviceId ||
      !deviceInfo.token ||
      !storeId ||
      !locale
    ) {
      throw new Error(
        'Error when making devices ms payload ' +
          JSON.stringify({
            deviceInfo,
            auth: storeId,
            locale,
          }),
      );
    }

    return {
      storeId: Number(storeId),
      token: deviceInfo.token,
      device_id: deviceInfo.deviceId,
      locale,
    };
  };

export const registerDeviceOnNuvemShop = async (): Promise<void> => {
  if (!IS_NATIVE_PLATFORM) return;

  const payload = await makeDevicesMsPayload();

  const body: RegisterDeviceRequestDto = {
    ...payload,
    platform_push: PlatformPushEnum.FCM,
  };

  try {
    axios.post('/v1/devices', body);
  } catch (error) {
    console.error(error);
  }
};

export const saveDeviceInfo = async (
  deviceInfo: DeviceStorageType,
): Promise<void> => {
  await setStorage(DEVICE_INFO, deviceInfo);
};

const DEVICE_ID_KEY = 'app_device_id';

export async function getPersistentDeviceId(): Promise<string> {
  try {
    try {
      const { value } = await SecureStoragePlugin.get({ key: DEVICE_ID_KEY });
      if (value) {
        return value;
      }
    } catch (storageError) {
      // do nothing
    }

    const { identifier } = await Device.getId();

    const success = await SecureStoragePlugin.set({
      key: DEVICE_ID_KEY,
      value: identifier,
    });

    if (!success) {
      throw new Error('Failed to save device id');
    }

    return identifier;
  } catch (err) {
    logError('Error getting persistent device id', err);
    const { identifier } = await Device.getId();
    return identifier;
  }
}
